import * as Blockly from 'blockly/core';
import { useBoardEditorStore } from '@/stores/useBoardEditorStore';
import { BoardType } from '@/(routers)/boards/editor/block/blockConfig';
import { BLOCK_TYPES } from '@/(routers)/boards/editor/block/blockRegistry';
import { blockSound } from '@/(routers)/boards/editor/block/blockSound';
import { validateAllBoardBlocks, validateI2CDevices } from '@/(routers)/boards/editor/block/blockValidator';
import { BOARD } from '@/(routers)/boards/editor/block/definitions/boards';
import { FieldOperatorDropdown } from '@/(routers)/boards/editor/block/definitions/fields/fieldOperatorDropdown';

export interface BlockIconDef {
    src: string;
    size: number;
}

export interface BlockDef {
    type: string;
    colour: string;
    strokeColour?: string;
    textColour?: string;
    useBoardColour?: boolean;
    tooltip?: string;
    deletable?: boolean;
    movable?: boolean;
    inline?: boolean;
    previousStatement?: boolean;
    nextStatement?: boolean;
    output?: string;
    outputShape?: number;
    inputs: InputDef[];
    onInit?: (this: Blockly.Block) => void;
    onBoardChange?: (block: Blockly.Block, board: BoardType) => void;
    onFieldChange?: (block: Blockly.Block, fieldName: string, newValue: string) => void;
}

export interface MutableBlockDef {
    type: string;
    colour: string;
    textColour?: string;
    tooltip?: string;
    deletable?: boolean;
    movable?: boolean;
    previousStatement?: boolean;
    nextStatement?: boolean;
    output?: string;
    init: (this: Blockly.Block) => void;
    mutationToDom: (this: Blockly.Block) => Element;
    domToMutation: (this: Blockly.Block, xml: Element) => void;
    onchange?: (this: Blockly.Block, e: Blockly.Events.Abstract) => void;
    onBoardChange?: (block: Blockly.Block, board: BoardType) => void;
}

export type ShadowDef = {
    type: string;
    fields?: Record<string, string | number>;
};

export type InputDef =
    | { kind: 'dummy'; fields: FieldDef[] }
    | { kind: 'statement'; name: string; fields?: FieldDef[] }
    | { kind: 'dummy_hidden'; name: string; fields: FieldDef[] }
    | {
          kind: 'value';
          name: string;
          check?: string | string[];
          fields?: FieldDef[];
          shadow?: ShadowDef;
      };

export type FieldDef =
    | { type: 'image'; icon: BlockIconDef; name?: string }
    | { type: 'label'; text: string; name?: string }
    | { type: 'number'; name: string; value: number }
    | {
          type: 'dropdown';
          name: string;
          options: { value: string; label: string; tooltip?: string }[];
          rectColour?: string;
      }
    | { type: 'change_board_dropdown'; name: string }
    | { type: 'field'; instance: Blockly.Field | (() => Blockly.Field); name?: string };

class FieldIconImage extends Blockly.FieldImage {
    private readonly iconSize_: number;

    constructor(src: string, size: number, alt: string) {
        super(src, size, size, alt);
        this.iconSize_ = size;
    }

    override getSize() {
        const size = super.getSize();
        size.width = this.iconSize_;
        return size;
    }

    override initView() {
        super.initView();
        if (this.imageElement) {
            // このerrorは大抵「画像が本当に存在しない」のではなく、直前のブロック再描画で
            // 読み込み中のリクエストが中断されただけ(実際に確認済み・タイミングを入れ替えるだけの
            // 対策では直りきらなかった)。恒久的なフォールバック画像に切り替える前に、本来のsrcで
            // 1回だけ読み込み直しを試みる。同じURL文字列を再設定してもブラウザが再取得しない
            // ことがあるため、末尾にクエリを付けて確実に別のリクエストとして扱わせる
            // (href属性を一旦空にする方式は、空文字列自体がerrorを誘発しかねないため避けた)。
            let hasRetried = false;
            this.imageElement.addEventListener('error', () => {
                const imageElement = this.imageElement;
                if (!imageElement) return;
                const intendedSrc = this.getValue();
                if (!hasRetried && intendedSrc) {
                    hasRetried = true;
                    const retrySrc = `${intendedSrc}${intendedSrc.includes('?') ? '&' : '?'}_retry=${Date.now()}`;
                    setTimeout(() => {
                        imageElement.setAttribute('href', retrySrc);
                        imageElement.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', retrySrc);
                    }, 100);
                    return;
                }
                imageElement.setAttribute('href', FALLBACK_ICON_SVG);
                imageElement.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', FALLBACK_ICON_SVG);
            });
        }
    }
}

const FALLBACK_ICON_SVG =
    'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40"><g transform="translate(20,20) scale(0.8)"><circle cx="0" cy="-10.7" r="3.082" fill="white"/><circle cx="0" cy="11.3" r="1.849" fill="white" opacity="0.2"/><circle cx="-7.6" cy="-7.6" r="2.774" fill="white" opacity="0.85"/><circle cx="7.6" cy="7.6" r="1.541" fill="white" opacity="0.35"/><circle cx="-10.7" cy="0" r="2.465" fill="white" opacity="0.7"/><circle cx="10.7" cy="0" r="1.232" fill="white" opacity="0.15"/><circle cx="-7.6" cy="7.6" r="2.157" fill="white" opacity="0.55"/><circle cx="7.6" cy="-7.6" r="0.924" fill="white" opacity="0.5"/></g></svg>';

export function blockIcon(icon: BlockIconDef): FieldIconImage {
    return new FieldIconImage(icon.src, icon.size, 'icon');
}

export const REGISTERED_BLOCK_TYPES: string[] = [];
export const BLOCK_DEFS = new Map<string, BlockDef | MutableBlockDef>();

const _boardChangeFlag = { value: false };
export function setChangingBoardFromBlock(v: boolean): void {
    _boardChangeFlag.value = v;
}
export function isChangingBoardFromBlock(): boolean {
    return _boardChangeFlag.value;
}

let isSyncingBoard = false;

export function syncAllBoardBlocks(ws: Blockly.WorkspaceSvg, value: string, playSoundOnce: boolean): void {
    isSyncingBoard = true;
    const board = value as BoardType;
    const color = BOARD[board].color;

    const flyoutWs = (ws as any).getFlyout?.()?.getWorkspace?.() as Blockly.WorkspaceSvg | null;
    const mainBlocks = ws.getAllBlocks(false);
    const flyoutBlocks = flyoutWs ? flyoutWs.getAllBlocks(false) : [];

    try {
        for (const b of mainBlocks) {
            if (b.type === BLOCK_TYPES.setup || b.type === BLOCK_TYPES.schoomy_board) {
                b.setColour(color);

                if (b.type === BLOCK_TYPES.schoomy_board) {
                    const boardField = b.getField('BOARD') as FieldOperatorDropdown | null;
                    if (boardField && boardField.getValue() !== value) boardField.setValue(value);

                    // BOARD_ICONのsrcはここでは設定しない。以前はここで直接setValue()していたが、
                    // 直後のws.render()(下のfinally内)がブロックのSVG構造を再生成し、たった今
                    // 読み込み始めた画像のリクエストを中断させてしまうことがあった。FieldIconImageは
                    // 画像読み込み失敗時に固定のフォールバック画像へ恒久的に切り替える作りのため、
                    // この中断がerrorイベントとして扱われ、アイコンが読み込み中のような見た目のまま
                    // 二度と戻らなくなる不具合があった(実際に発生)。render()が完全に終わった後に
                    // 設定するよう、下のrequestAnimationFrameコールバック内に移動する。

                    requestAnimationFrame(() => {
                        boardField?.refreshStyle();
                    });
                }
            }

            BLOCK_DEFS.get(b.type)?.onBoardChange?.(b, board);
        }

        Blockly.Events.disable();
        try {
            for (const b of flyoutBlocks) {
                BLOCK_DEFS.get(b.type)?.onBoardChange?.(b, board);
            }
        } finally {
            Blockly.Events.enable();
        }

        validateAllBoardBlocks(ws, board);
        validateI2CDevices(ws);
    } finally {
        isSyncingBoard = false;
        requestAnimationFrame(() => {
            ws.render();
            // render()でSVG構造が確定した後に、アイコン画像のsrcを設定する(上記コメント参照)。
            for (const b of mainBlocks) {
                if (b.type === BLOCK_TYPES.schoomy_board) {
                    const iconField = b.getField('BOARD_ICON') as Blockly.FieldImage | null;
                    if (iconField) iconField.setValue(BOARD[board].icon.src);
                }
                if (b.type === BLOCK_TYPES.setup) {
                    const setupIconField = b.getField('SETUP_ICON') as Blockly.FieldImage | null;
                    if (setupIconField) setupIconField.setValue(BOARD[board].setupIcon.src);
                }
            }
        });
    }
    if (playSoundOnce) blockSound.queue('board-change');
}

const shadowDomCache = new Map<string, Element>();

function buildShadowDom(shadow: ShadowDef): Element {
    const cacheKey = JSON.stringify(shadow);
    const cached = shadowDomCache.get(cacheKey);
    if (cached) return cached.cloneNode(true) as Element;

    const shadowDom = Blockly.utils.xml.createElement('shadow');
    shadowDom.setAttribute('type', shadow.type);
    for (const [k, v] of Object.entries(shadow.fields ?? {})) {
        const fieldEl = Blockly.utils.xml.createElement('field');
        fieldEl.setAttribute('name', k);
        fieldEl.textContent = String(v);
        shadowDom.appendChild(fieldEl);
    }

    shadowDomCache.set(cacheKey, shadowDom);
    return shadowDom.cloneNode(true) as Element;
}

export function createBlock(def: BlockDef): void {
    REGISTERED_BLOCK_TYPES.push(def.type);
    if (def.onBoardChange) BLOCK_DEFS.set(def.type, def);

    const boardOptions = [
        { value: 'orange', label: 'オレンジ' },
        { value: 'blue', label: 'ブルー' },
    ] as const;

    Blockly.Blocks[def.type] = {
        init(this: Blockly.Block) {
            for (const input of def.inputs) {
                let row: Blockly.Input;

                if (input.kind === 'dummy' || input.kind === 'dummy_hidden') {
                    row = this.appendDummyInput(input.kind === 'dummy_hidden' ? input.name : undefined);
                    if (input.kind === 'dummy_hidden') row.setVisible(false);
                } else if (input.kind === 'statement') {
                    row = this.appendStatementInput(input.name);
                } else {
                    row = this.appendValueInput(input.name);
                    if (input.check) row.setCheck(input.check);
                    if (input.shadow) {
                        row.connection?.setShadowDom(buildShadowDom(input.shadow));
                    }
                }

                for (const field of input.fields ?? []) {
                    if (field.type === 'image') {
                        row.appendField(blockIcon(field.icon), field.name);
                    } else if (field.type === 'label') {
                        row.appendField(field.text, field.name);
                    } else if (field.type === 'number') {
                        row.appendField(new Blockly.FieldNumber(field.value, 0, undefined, 1), field.name);
                    } else if (field.type === 'dropdown') {
                        row.appendField(
                            new FieldOperatorDropdown(field.options, undefined, undefined, field.rectColour),
                            field.name,
                        );
                    } else if (field.type === 'field') {
                        const inst = typeof field.instance === 'function' ? field.instance() : field.instance;
                        row.appendField(inst, field.name);
                    } else if (field.type === 'change_board_dropdown') {
                        row.appendField(
                            new FieldOperatorDropdown(
                                boardOptions as unknown as { value: string; label: string }[],
                                function (this: FieldOperatorDropdown, value: string) {
                                    if (isSyncingBoard) return value;

                                    const block = this.getSourceBlock();
                                    if (!block) return value;

                                    const prevValue = this.getValue();
                                    if (!prevValue || prevValue === value) return value;

                                    requestAnimationFrame(() => {
                                        const ws = block.workspace as Blockly.WorkspaceSvg;
                                        setChangingBoardFromBlock(true);
                                        useBoardEditorStore.getState().setBoard(value as BoardType);
                                        setTimeout(() => setChangingBoardFromBlock(false), 200);
                                        syncAllBoardBlocks(ws, value, true);
                                    });

                                    return value;
                                },
                            ),
                            field.name,
                        );
                    }
                }
            }

            const currentBoard = useBoardEditorStore.getState().board;
            this.setColour(def.useBoardColour ? BOARD[currentBoard].color : def.colour);
            this.setTooltip(def.tooltip ?? '');
            this.setMovable(def.movable ?? true);
            this.setDeletable(def.deletable ?? true);
            if (def.inline) this.setInputsInline(true);

            if (def.output) {
                this.setPreviousStatement(false);
                this.setNextStatement(false);
                this.setOutput(true, def.output);
                if (def.outputShape !== undefined) {
                    this.setOutputShape(def.outputShape);
                }
            } else {
                this.setPreviousStatement(def.previousStatement ?? false);
                this.setNextStatement(def.nextStatement ?? false);
            }

            if (def.useBoardColour) {
                const boardField = this.getField('BOARD') as FieldOperatorDropdown | null;
                if (boardField) boardField.doValueUpdate_(currentBoard);

                const iconField = this.getField('BOARD_ICON') as Blockly.FieldImage | null;
                if (iconField) iconField.setValue(BOARD[currentBoard].icon.src);

                const setupIconField = this.getField('SETUP_ICON') as Blockly.FieldImage | null;
                if (setupIconField) setupIconField.setValue(BOARD[currentBoard].setupIcon.src);
            }

            if (def.strokeColour) {
                requestAnimationFrame(() => {
                    (this as any).pathObject?.svgPath?.setAttribute('stroke', def.strokeColour);
                });
            }

            if (def.onFieldChange) {
                this.setOnChange((e: Blockly.Events.Abstract) => {
                    if (!(e instanceof Blockly.Events.BlockChange)) return;
                    if (e.blockId !== this.id) return;
                    if (e.element !== 'field') return;
                    if (!e.name) return;
                    def.onFieldChange!(this, e.name, e.newValue as string);
                });
            }

            def.onInit?.call(this);

            if (def.onBoardChange) {
                def.onBoardChange(this, currentBoard);
            }

            if (def.outputShape !== undefined) {
                this.setOutputShape(def.outputShape);
                if (this.isShadow()) {
                    (this as Blockly.BlockSvg).render();
                }
            }
        },
    };
}

export function createMutableBlock(def: MutableBlockDef): void {
    REGISTERED_BLOCK_TYPES.push(def.type);
    if (def.onBoardChange) BLOCK_DEFS.set(def.type, def);

    Blockly.Blocks[def.type] = {
        init: def.init,
        mutationToDom: def.mutationToDom,
        domToMutation: def.domToMutation,
        ...(def.onchange ? { onchange: def.onchange } : {}),
    };
}
